import mergeRefs from 'merge-refs';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useController } from 'react-hook-form';
import { useEventListener } from 'usehooks-ts';
import SymbolMenu, { type SymbolMenuActions } from '@/components/symbol/symbol-menu';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';
import QuillEditor, { type EditorActions } from './quill-editor';

type FormTextAreaProps = {
  disabled?: boolean;
  initialValue?: string;
  isLoading?: boolean;
  onChange?: (value: string) => void;
};

const ConversationTextArea = forwardRef<EditorActions, FormTextAreaProps>(({ initialValue, disabled }, ref) => {
  const {
    form: { control, getValues, reset },
  } = useConversationForm();
  const { handleSubmit } = useConversationHandlers();

  const editorRef = useRef<EditorActions>(null);
  const symbolMenuRef = useRef<SymbolMenuActions>(null);
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: 'Content',
    defaultValue: '',
  });

  const handleEmoji = (emoji: string) => {
    editorRef.current?.insertText(emoji);
  };

  const handleSticker = async (sticker: string) => {
    const quote = getValues('Quote');
    await handleSubmit({
      Content: sticker,
      Quote: quote,
      ContentType: ConversationMessageContentType.Emoticon,
    });
    reset();
    symbolMenuRef.current?.close();
  };

  useImperativeHandle(ref, () => editorRef.current || ({} as EditorActions));

  useEventListener(
    'visibilitychange',
    () => {
      editorRef.current?.blur();
    },
    useRef(document),
  );

  return (
    <div
      className={cn(
        'flex min-h-10 w-full flex-1 items-center transition-colors desktop:gap-4 textarea desktop:bg-transparent ring-primary border-none overflow-hidden p-0',
        error && 'textarea-error',
      )}>
      <QuillEditor
        ref={mergeRefs(editorRef, field.ref)}
        initialValue={initialValue}
        onFocus={() => symbolMenuRef.current?.close()}
      />

      <SymbolMenu
        ref={symbolMenuRef}
        disabled={disabled}
        onEmoji={handleEmoji}
        onSticker={handleSticker}
      />
      <div className='hidden h-8 w-px bg-base-200 desktop:block self-end' />
    </div>
  );
});

ConversationTextArea.displayName = 'ConversationTextArea';

export default ConversationTextArea;
